// pages/reports/ExpenseReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    ChevronUp,
    RotateCcw,
    DollarSign,

    TrendingDown,
    PieChart,
    Calendar,
    Folder,
    CreditCard,
    RefreshCw,
    BarChart2,

    Tag
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const ExpenseReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };

    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };

            const result = await reportActions.getExpenseReport(params);

            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // Get filtered expenses by category
    const getFilteredExpenses = () => {
        if (!reportData?.top_expenses) return [];
        if (categoryFilter === 'all') return reportData.top_expenses;
        return reportData.top_expenses.filter(e => e.category_id == categoryFilter);
    };

    // Render filter bar
    const renderFilterBar = () => {
        const categoryOptions = [
            { value: 'all', label: 'All Categories' },
            ...(reportData?.category_breakdown?.map(cat => ({
                value: cat.id,
                label: cat.category_name
            })) || [])
        ];

        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Category</label>
                            <Select
                                options={categoryOptions}
                                value={categoryOptions.find(opt => opt.value === categoryFilter)}
                                onChange={(opt) => setCategoryFilter(opt?.value || 'all')}
                                placeholder="Select category"
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;

        const { summary } = reportData;

        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Expenses</p>
                                    <h3 className="mb-0 text-danger">
                                        {reportActions.formatCurrency(summary.total_amount)}
                                    </h3>
                                    <small className="text-muted">{summary.total_expenses} transactions</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Categories Used</p>
                                    <h3 className="mb-0 text-primary">
                                        {summary.total_categories}
                                    </h3>
                                    <small className="text-muted">Active categories</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <Folder size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Average Expense</p>
                                    <h3 className="mb-0 text-info">
                                        {reportActions.formatCurrency(summary.average_amount)}
                                    </h3>
                                    <small className="text-muted">Per transaction</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <BarChart2 size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Payment Methods</p>
                                    <h3 className="mb-0 text-success">
                                        {summary.total_payment_methods}
                                    </h3>
                                    <small className="text-muted">Used this period</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <CreditCard size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'daily', label: 'Daily Breakdown', icon: <Calendar size={16} /> },
            { id: 'categories', label: 'By Category', icon: <Folder size={16} /> },
            { id: 'payments', label: 'Payment Methods', icon: <CreditCard size={16} /> },
            { id: 'status', label: 'By Status', icon: <Tag size={16} /> },
            { id: 'top', label: 'Top Expenses', icon: <TrendingDown size={16} /> }
        ];

        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };

    // Render summary tab
    const renderSummaryTab = () => {
        if (!reportData) return null;

        const topCategories = reportData.category_breakdown?.slice(0, 5) || [];

        return (
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Expense Overview</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Total Expense Transactions</td>
                                        <td className="text-end"><strong>{reportData.summary.total_expenses}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Categories Used</td>
                                        <td className="text-end"><strong>{reportData.summary.total_categories}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Maximum Expense Amount</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(reportData.summary.max_amount)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Minimum Expense Amount</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(reportData.summary.min_amount)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Average Expense Amount</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(reportData.summary.average_amount)}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Top Categories</h6>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th className="text-end">Count</th>
                                            <th className="text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topCategories.map((cat, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <span
                                                        className="badge me-2"
                                                        style={{ backgroundColor: cat.color + '20', color: cat.color }}
                                                    >
                                                        {cat.icon}
                                                    </span>
                                                    {cat.category_name}
                                                </td>
                                                <td className="text-end">{cat.expense_count}</td>
                                                <td className="text-end">{reportActions.formatCurrency(cat.total_amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render daily tab
    const renderDailyTab = () => {
        if (!reportData?.daily_breakdown || reportData.daily_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No daily expense data available for the selected period.
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Daily Expense Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th className="text-end">Expenses</th>
                                    <th className="text-end">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.daily_breakdown.map((day, idx) => (
                                    <tr key={idx}>
                                        <td>{reportActions.formatDate(day.date)}</td>
                                        <td className="text-end">{day.expense_count}</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(day.total_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th>Total</th>
                                    <th className="text-end">
                                        {reportData.daily_breakdown.reduce((sum, d) => sum + d.expense_count, 0)}
                                    </th>
                                    <th className="text-end text-danger">
                                        {reportActions.formatCurrency(
                                            reportData.daily_breakdown.reduce((sum, d) => sum + d.total_amount, 0)
                                        )}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render categories tab
    const renderCategoriesTab = () => {
        if (!reportData?.category_breakdown || reportData.category_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No category data available for the selected period.
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Category-wise Expenses</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Category</th>
                                    <th className="text-end">Expenses</th>
                                    <th className="text-end">Total Amount</th>
                                    <th className="text-end">Average</th>
                                    <th className="text-end">Max Amount</th>
                                    <th className="text-end">% of Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.category_breakdown.map((cat, idx) => {
                                    const percentage = (cat.total_amount / reportData.summary.total_amount) * 100;
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <span
                                                        className="badge me-2 p-2"
                                                        style={{ backgroundColor: cat.color + '20', color: cat.color }}
                                                    >
                                                        {cat.icon}
                                                    </span>
                                                    <strong>{cat.category_name}</strong>
                                                </div>
                                            </td>
                                            <td className="text-end">{cat.expense_count}</td>
                                            <td className="text-end text-danger">{reportActions.formatCurrency(cat.total_amount)}</td>
                                            <td className="text-end">{reportActions.formatCurrency(cat.average_amount)}</td>
                                            <td className="text-end">{reportActions.formatCurrency(cat.max_amount)}</td>
                                            <td className="text-end">{percentage.toFixed(2)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render payments tab
    const renderPaymentsTab = () => {
        if (!reportData?.payment_breakdown || reportData.payment_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No payment method data available.
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Payment Method Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Payment Method</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Total Amount</th>
                                    <th className="text-end">Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.payment_breakdown.map((payment, idx) => {
                                    const percentage = (payment.total_amount / reportData.summary.total_amount) * 100;
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <span className="badge bg-secondary">
                                                    {payment.payment_method?.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className="text-end">{payment.expense_count}</td>
                                            <td className="text-end text-danger">{reportActions.formatCurrency(payment.total_amount)}</td>
                                            <td className="text-end">{percentage.toFixed(2)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render status tab
    const renderStatusTab = () => {
        if (!reportData?.status_breakdown || reportData.status_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No status data available.
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Status Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Status</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.status_breakdown.map((status, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <span className={`badge ${status.status === 'paid' ? 'bg-success' :
                                                status.status === 'pending' ? 'bg-warning' : 'bg-danger'
                                                }`}>
                                                {status.status?.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="text-end">{status.expense_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(status.total_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render top expenses tab
    const renderTopExpensesTab = () => {
        const filteredExpenses = getFilteredExpenses();

        if (filteredExpenses.length === 0) {
            return (
                <div className="alert alert-info">
                    No expenses found for the selected category.
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Top 20 Expenses</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th>Expense No</th>
                                    <th>Category</th>
                                    <th>Paid To</th>
                                    <th>Description</th>
                                    <th>Payment Method</th>
                                    <th className="text-end">Amount</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExpenses.map((expense, idx) => (
                                    <tr key={idx}>
                                        <td>{reportActions.formatDate(expense.expense_date)}</td>
                                        <td>
                                            <strong>{expense.expense_no}</strong>
                                        </td>
                                        <td>
                                            <span className="badge bg-secondary">
                                                {expense.category_name || 'Uncategorized'}
                                            </span>
                                        </td>
                                        <td>{expense.paid_to || '-'}</td>
                                        <td>{expense.description || '-'}</td>
                                        <td>
                                            <span className="badge bg-info">
                                                {expense.payment_method?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="text-end text-danger fw-bold">
                                            {reportActions.formatCurrency(expense.amount)}
                                        </td>
                                        <td>
                                            <span className={`badge ${expense.status === 'paid' ? 'bg-success' :
                                                expense.status === 'pending' ? 'bg-warning' : 'bg-danger'
                                                }`}>
                                                {expense.status?.toUpperCase()}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);

    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Expense Report...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Expense Report</h4>
                            <h6>Track and analyze business expenses</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                    <div>
                                        <strong>Total Expenses:</strong> {reportActions.formatCurrency(reportData?.summary.total_amount || 0)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Bar */}
                {renderFilterBar()}

                {/* Summary Cards */}
                {renderSummaryCards()}

                {/* Tabs */}
                {renderTabs()}

                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'daily' && renderDailyTab()}
                {activeTab === 'categories' && renderCategoriesTab()}
                {activeTab === 'payments' && renderPaymentsTab()}
                {activeTab === 'status' && renderStatusTab()}
                {activeTab === 'top' && renderTopExpensesTab()}
            </div>
        </div>
    );
};

export default ExpenseReport;